import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function BulkWatermark({ onClose, onDone }) {
  const [items, setItems] = useState(null);
  const [checked, setChecked] = useState({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState("");
  const [results, setResults] = useState([]);
  const cancelRef = useRef(false);

  useEffect(() => {
    api.get("/products/watermark-candidates")
      .then((r) => {
        setItems(r.data);
        setChecked(Object.fromEntries(r.data.map((p) => [p.id, true])));
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Gagal memuat daftar produk");
        setItems([]);
      });
  }, []);

  const selected = (items || []).filter((p) => checked[p.id]);
  const okCount = results.filter((r) => r.ok).length;
  const failCount = results.filter((r) => !r.ok).length;
  const finished = results.length > 0 && progress >= selected.length;

  const start = async () => {
    if (!selected.length || running) return;
    setRunning(true);
    cancelRef.current = false;
    for (let i = progress; i < selected.length; i++) {
      if (cancelRef.current) break;
      const p = selected[i];
      setCurrent(p.name);
      setResults((r) => [...r.filter((x) => x.id !== p.id), { id: p.id, name: p.name, ok: null }]);
      try {
        await api.post(`/products/${p.id}/remove-watermark`, {}, { timeout: 300000 });
        setResults((r) => [...r.filter((x) => x.id !== p.id), { id: p.id, name: p.name, ok: true }]);
      } catch (e) {
        setResults((r) => [...r.filter((x) => x.id !== p.id), { id: p.id, name: p.name, ok: false, msg: e?.response?.data?.detail || "gagal" }]);
      }
      setProgress(i + 1);
    }
    setRunning(false);
    setCurrent("");
    onDone?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={running ? undefined : onClose}>
      <div data-testid="bulk-wm-modal" className="bg-[#F6F1E7] rounded-2xl max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-black text-lg">✨ Hapus Watermark Massal</h3>
          {!running && <button data-testid="bulk-wm-close-x" onClick={onClose} className="p-1 rounded-full hover:bg-stone-200"><X className="w-5 h-5" /></button>}
        </div>
        <p className="text-xs text-stone-500 mb-3">AI membersihkan watermark "Klik INDOGROSIR" dari foto produk satu per satu (±1 menit/foto, memakai kredit LLM key). Foto produk otomatis diganti versi bersih.</p>

        {!items && <p className="text-sm text-stone-400">Memuat daftar produk…</p>}
        {items && !items.length && <p data-testid="bulk-wm-empty" className="text-sm text-stone-400 bg-white rounded-xl border border-[#E5DECF] p-4">Tidak ada produk bergambar.</p>}

        {items && items.length > 0 && (
          <>
            <div className="flex items-center justify-between text-xs font-bold text-stone-600 mb-2">
              <span data-testid="bulk-wm-count">{selected.length} dari {items.length} produk dipilih</span>
              {!running && results.length === 0 && (
                <button data-testid="bulk-wm-toggle-all" className="text-emerald-700 underline" onClick={() => setChecked(Object.fromEntries(items.map((p) => [p.id, Object.values(checked).every((v) => !v)])))}>
                  {Object.values(checked).every((v) => !v) ? "Pilih semua" : "Kosongkan"}
                </button>
              )}
            </div>

            {(running || results.length > 0) && (
              <div className="mb-3">
                <div className="h-2.5 rounded-full bg-stone-200 overflow-hidden">
                  <div data-testid="bulk-wm-progress" className="h-full bg-emerald-600 transition-all" style={{ width: `${selected.length ? (progress / selected.length) * 100 : 0}%` }} />
                </div>
                <p className="text-xs text-stone-500 mt-1" data-testid="bulk-wm-status">
                  {running ? `Memproses ${progress + 1}/${selected.length}: ${current}…` : finished ? `Selesai — ✓ ${okCount} berhasil, ✗ ${failCount} gagal` : `Berhenti di ${progress}/${selected.length}`}
                </p>
              </div>
            )}

            <ul className="space-y-1 max-h-56 overflow-y-auto mb-3">
              {(running || results.length > 0 ? selected : items).map((p) => {
                const r = results.find((x) => x.id === p.id);
                return (
                  <li key={p.id} data-testid={`bulk-wm-item-${p.id}`} className="flex items-center gap-2 bg-white rounded-lg border border-[#E5DECF] px-2.5 py-1.5 text-xs">
                    {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
                    <span className="flex-1 truncate font-semibold">{p.name}</span>
                    {r ? (
                      <span className={r.ok === null ? "text-amber-600 font-bold animate-pulse" : r.ok ? "text-emerald-700 font-bold" : "text-rose-600 font-bold"}>
                        {r.ok === null ? "⏳" : r.ok ? "✓ Bersih" : `✗ ${r.msg || "Gagal"}`}
                      </span>
                    ) : (
                      !running && <input type="checkbox" data-testid={`bulk-wm-check-${p.id}`} checked={!!checked[p.id]} onChange={(e) => setChecked((c) => ({ ...c, [p.id]: e.target.checked }))} className="w-4 h-4 accent-emerald-700" />
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="flex gap-2">
              {!running && !finished && (
                <button data-testid="bulk-wm-start-btn" onClick={start} disabled={!selected.length} className="flex-1 py-2.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold disabled:opacity-50">
                  {progress > 0 ? "Lanjutkan" : "Mulai Hapus Watermark"}
                </button>
              )}
              {running && (
                <button data-testid="bulk-wm-cancel-btn" onClick={() => { cancelRef.current = true; }} className="flex-1 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold">
                  Berhenti Setelah Foto Ini
                </button>
              )}
              {!running && (
                <button data-testid="bulk-wm-close-btn" onClick={onClose} className="px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-stone-700">
                  Tutup
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
